import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ApiFailure, authApi, type MfaSetupResponse } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import { AuthShell } from '@/components/ui/AuthShell';
import { Banner } from '@/components/ui/Banner';
import { Button } from '@/components/ui/Button';
import { Input, Label } from '@/components/ui/Input';

export function MfaSetupPage() {
  const navigate = useNavigate();
  const challengeToken = useAuthStore((s) => s.challengeToken);
  const accessToken = useAuthStore((s) => s.accessToken);
  const setAuthenticated = useAuthStore((s) => s.setAuthenticated);

  const [setup, setSetup] = useState<MfaSetupResponse | null>(null);
  const [loadingSetup, setLoadingSetup] = useState(true);
  const [setupError, setSetupError] = useState<string | null>(null);

  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [recoveryConfirmed, setRecoveryConfirmed] = useState(false);

  useEffect(() => {
    if (!challengeToken) return;
    let cancelled = false;
    authApi
      .mfaSetup(challengeToken)
      .then((s) => {
        if (!cancelled) setSetup(s);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setSetupError(
          err instanceof ApiFailure ? err.message : 'Impossible de démarrer le setup 2FA',
        );
      })
      .finally(() => {
        if (!cancelled) setLoadingSetup(false);
      });
    return () => {
      cancelled = true;
    };
  }, [challengeToken]);

  // Cf MfaVerifyPage : on prévient la boucle post-setAuthenticated en
  // priorisant l'accessToken sur l'absence de challenge.
  if (accessToken) return <Navigate to="/" replace />;
  if (!challengeToken) return <Navigate to="/login" replace />;

  async function handleVerify(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (submitting || !setup) return;
    setVerifyError(null);
    if (!/^\d{6}$/.test(code)) {
      setVerifyError('Le code doit contenir 6 chiffres.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await authApi.mfaVerify(challengeToken!, code);
      setAuthenticated(res);
      navigate('/', { replace: true });
    } catch (err) {
      setVerifyError(
        err instanceof ApiFailure
          ? err.code === 'UNAUTHORIZED'
            ? "Code invalide. Vérifiez l'horloge de votre appareil."
            : err.message
          : 'Erreur réseau - merci de réessayer',
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (loadingSetup) {
    return (
      <AuthShell title="Configuration 2FA">
        <p className="text-sm text-text-muted">Préparation…</p>
      </AuthShell>
    );
  }
  if (setupError || !setup) {
    return (
      <AuthShell title="Configuration 2FA">
        <Banner tone="danger">{setupError ?? 'Setup 2FA indisponible'}</Banner>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Configurer la 2FA"
      subtitle="Scannez le QR code avec Google / Microsoft Authenticator puis confirmez avec un code."
    >
      <div className="mb-6 flex justify-center">
        <img
          src={setup.qrCodeDataUrl}
          alt="QR code TOTP"
          className="h-48 w-48 rounded-md border border-border-subtle"
        />
      </div>

      <Banner tone="warning" title="Codes de récupération - à conserver" className="mb-6">
        <p className="mb-2">
          Ces codes ne seront plus affichés. Chacun est utilisable une seule fois en cas de perte de
          votre appareil 2FA.
        </p>
        <ul className="grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-xs">
          {setup.recoveryCodes.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
        <label className="mt-3 flex items-start gap-2 text-xs">
          <input
            type="checkbox"
            checked={recoveryConfirmed}
            onChange={(e) => setRecoveryConfirmed(e.target.checked)}
            className="mt-0.5 accent-accent"
          />
          <span>J&apos;ai copié ces codes dans un endroit sûr.</span>
        </label>
      </Banner>

      <form onSubmit={handleVerify} className="space-y-4" noValidate>
        <div>
          <Label htmlFor="code" required>
            Code à 6 chiffres affiché par l&apos;application
          </Label>
          <Input
            id="code"
            name="code"
            type="text"
            inputMode="numeric"
            pattern="\d{6}"
            maxLength={6}
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            className="text-center tracking-[0.4em] font-mono text-lg"
            required
          />
        </div>

        {verifyError && <Banner tone="danger">{verifyError}</Banner>}

        <Button
          type="submit"
          variant="primary"
          className="w-full"
          loading={submitting}
          disabled={code.length !== 6 || !recoveryConfirmed}
        >
          {submitting ? 'Vérification…' : 'Activer la 2FA et se connecter'}
        </Button>
      </form>
    </AuthShell>
  );
}
